import { FunctionComponent, ButtonHTMLAttributes } from "react";

import styled, { css, keyframes } from "styled-components";

import { colors } from "@src/packages/chime-ui/system";
import getConfig from "next/config";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  theme?: any;
  borders?: boolean;
  className?: string;
  loading?: boolean;
}

export const ButtonBase: FunctionComponent<ButtonProps> = ({ loading, children, ...props }) => (
  <button {...props}>{loading ? <ButtonSpinnerIcon /> : children}</button>
);

export const buttonMixin = css<ButtonProps>`
  display: block;
  min-width: 80px;
  width: 100%;
  height: 3rem;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
  text-shadow: none;
  line-height: 1.5rem;
  border-radius: 1.5rem;
  box-shadow: none;
  transition: all 0.3s ease 0s;
  padding: 0.75rem 1rem;
  color: ${colors.notBlack};
  background-color: ${colors.brightGreen};
  border: none;
  cursor: pointer;
  text-decoration: none;
  text-align: center;
  box-sizing: border-box;
  margin: 24px auto 5px;

  &:focus {
    outline: none;
    box-shadow: 0 4px 12px rgba(51, 51, 51, 0.24);
  }

  &:active {
    outline: none;
    opacity: 0.6;
  }

  ${props =>
    (props.disabled || props.loading) &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `};
`;

export const Button = styled(ButtonBase)`
  ${buttonMixin}
`;

export const AltButton = styled(Button)`
  background-color: ${colors.white};
  box-shadow: 0 4px 12px rgba(51, 51, 51, 0.16);
`;

export const SecondaryButton = styled(Button)`
  color: ${colors.darkGreen};
  background: none;
`;

export const ghostButtonMixin = css`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
`;

export const GhostButton = styled.button`
  ${ghostButtonMixin}
`;

export const UnderlinedGhostButton = styled.button`
  ${ghostButtonMixin};
  text-decoration: underline;
  font-weight: 700;
`;

export const UnderlinedBoldGhostButton = styled.button`
  ${ghostButtonMixin};
  text-decoration: underline;
  font-weight: 700;
`;

export const rotate360 = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const {
  publicRuntimeConfig: { basePath },
} = getConfig();

export const ButtonSpinnerIcon = styled.img.attrs({
  "aria-label": "Spinner Icon",
  src: `${basePath}/icons/loadingSpinner.png`,
})`
  width: 1.5rem;
  height: 1.5rem;
  animation: ${rotate360} 1s steps(8, end) infinite;
`;
